import { useEffect, useState } from 'react';
import {
  Button,
  cn,
  Combobox,
  DatePicker,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  Input,
  NativeSelect,
  RadioCardGroup,
  RadioCardItem,
  RichTextEditor,
} from '@eureca/ui';
import { XIcon } from './icons';
import { UNLOCKED_RESUBMIT, type ResubmitLock } from './resubmit-lock';

// SPA token set for the stage-type picker. `'form'` (rename de `'formulario'`
// no refactor absorbs-journey-trail) é o único type que persiste hoje. Os
// outros 5 (`'complementacao-perfil'`, `'curriculo'`, `'teste'`, `'fator-h'`,
// `'conteudo'`) estão no picker mas a BFF Zod (`JOB_STAGE_TYPES`) só aceita
// `'form'` — escrever os outros vai 400 até a BFF expandir + os módulos terem
// write-path no Core (ver docs/core-journey-stage-modals.md).
export type StageType =
  | 'form'
  | 'complementacao-perfil'
  | 'curriculo'
  | 'teste'
  | 'teste-externo'
  | 'fator-h'
  | 'conteudo';

export interface PhaseStageDraft {
  name: string;
  type: string;
  formId?: string;
  testId?: string;
  fatorHId?: string;
  contentText?: string;
  contentFileName?: string | null;
  /**
   * Etapa de teste externo (e Fator H): modo de exibição e URL da plataforma.
   * `'embed'` → iframe + "Validar respostas"; `'nova-aba'` → abre em nova aba
   * com salvamento via webhook; `'redirect'` → navega na MESMA aba e a
   * plataforma redireciona de volta ao concluir (Kolab bloqueia iframe via
   * X-Frame-Options, então é a alternativa integrada). Persistidos em
   * `module.config` (`embedMode` boolean + `displayMode`).
   */
  externalEmbedMode?: 'embed' | 'nova-aba' | 'redirect' | '';
  externalUrl?: string;
  /**
   * EU-539 — plataforma da etapa `teste-externo`: `'url'` (genérica, o
   * consultor digita a URL) ou `'kolab'` (instância escolhida no catálogo da
   * Kolab; a URL é gerada por candidato em runtime). Persistido em
   * `module.config.platform`.
   */
  externalPlatform?: 'url' | 'kolab' | '';
  /** EU-539 — instância Kolab escolhida (persistida em `module.config.kolabInstanceId`). */
  kolabInstanceId?: string;
  /** URL pública da mídia uploadada (preenchida após o upload concluir). */
  contentFileUrl?: string | null;
  /** Tipo da mídia uploadada (`pdf`/`video`), derivado do MIME no upload. */
  contentFileType?: 'pdf' | 'video' | null;
  /** `true` enquanto a mídia está sendo enviada ao BFF (trava o submit). */
  contentUploading?: boolean;
  startDate: string;
  endDate: string;
  endTime?: string;
  canResubmit: 'sim' | 'nao' | '';
}

export interface SheetSelectOption {
  value: string;
  label: string;
  /**
   * EU-764 — política de reenvio do instrumento, vinda do Core. `false` trava
   * o campo "Será possível reenviar a resposta?" em "Não".
   */
  canResubmit?: boolean | null;
  /** `false` = o teste não se aplica a esta vaga; a opção fica desabilitada. */
  eligible?: boolean;
  /** Por que está desabilitada — vai para o tooltip e o `aria-describedby`. */
  ineligibleReason?: 'apprentice_job' | null;
}

/** RN-20 — copy literal do card. */
const TEST_INELIGIBLE_COPY = 'Não disponível para vagas de aprendiz';
const TESTS_LOAD_ERROR_COPY = 'Não foi possível carregar os testes. Tente de novo.';

export type PhaseSheetMode = 'add' | 'edit';

export interface PhaseSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  values: PhaseStageDraft;
  errors: Record<string, string>;
  isSubmitting: boolean;
  formOptions: SheetSelectOption[];
  testOptions: SheetSelectOption[];
  /** RN-17 — a lista de testes ainda está carregando. */
  testsLoading?: boolean;
  /** RN-17 — a chamada do catálogo falhou (estado distinto de "lista vazia"). */
  testsError?: boolean;
  fatorHOptions: SheetSelectOption[];
  /** EU-539 — instâncias Kolab (catálogo dinâmico; nunca hardcoded). */
  kolabOptions: SheetSelectOption[];
  /**
   * EU-539 — estado do catálogo Kolab, para a UI distinguir loading /
   * vazio / erro de API no select de instâncias.
   */
  kolabCatalogueState: 'loading' | 'error' | 'ready';
  onFieldChange: <K extends keyof PhaseStageDraft>(field: K, value: PhaseStageDraft[K]) => void;
  onContentFileChange: (file: File | null) => void;
  onSubmit: () => void;
  mode?: PhaseSheetMode;
  /**
   * RN-13/RN-14 — trava do campo "Será possível reenviar a resposta?".
   * Computado pelo container a partir do tipo da etapa + teste selecionado
   * (ver `resubmit-lock.ts`). Default destravado.
   */
  resubmitLock?: ResubmitLock;
  /**
   * `true` quando a etapa editada já tem respostas de candidatos
   * (`answerCount > 0`). Trava o **Tipo** e o **catálogo** (Formulário/Teste/
   * Fator H) — trocar a identidade reataria as respostas existentes a outra
   * etapa. Nome/datas/horário/etc. seguem editáveis. Defesa server-side no
   * Core (422 `module_type_locked_has_answers`).
   */
  hasAnswers?: boolean;
}

const STAGE_TYPE_OPTIONS: readonly { value: StageType; label: string }[] = [
  { value: 'complementacao-perfil', label: 'Complementação de perfil' },
  { value: 'form', label: 'Formulário' },
  { value: 'curriculo', label: 'Currículo' },
  { value: 'teste', label: 'Teste' },
  { value: 'teste-externo', label: 'Teste externo' },
  { value: 'fator-h', label: 'Fator H' },
  { value: 'conteudo', label: 'Conteúdo' },
];

export function PhaseSheet({
  open,
  onOpenChange,
  values,
  errors,
  isSubmitting,
  formOptions,
  testOptions,
  testsLoading = false,
  testsError = false,
  fatorHOptions,
  kolabOptions,
  kolabCatalogueState,
  onFieldChange,
  onContentFileChange,
  onSubmit,
  mode = 'add',
  resubmitLock = UNLOCKED_RESUBMIT,
  hasAnswers = false,
}: PhaseSheetProps) {
  const isEdit = mode === 'edit';
  const title = isEdit ? 'Editar etapa' : 'Adicionar nova etapa';
  const submitLabel = isEdit ? 'Salvar alterações' : 'Adicionar etapa';
  // RN-13/RN-14 — quando o campo está travado, o radio exibe o valor imposto
  // pela regra de negócio em vez do valor do draft.
  const resubmitValue: PhaseStageDraft['canResubmit'] = resubmitLock.locked
    ? (resubmitLock.value ?? '')
    : values.canResubmit;
  // EU-165 Juliana TODO #3c — erro local de upload (extensão/tamanho), reseta
  // quando o modal fecha ou o arquivo válido é selecionado.
  const [fileError, setFileError] = useState<string>('');
  useEffect(() => {
    if (!open) setFileError('');
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className={cn(
          'gap-0 p-0',
          'lg:left-auto lg:right-0 lg:top-0 lg:bottom-0',
          'lg:h-dvh lg:max-h-dvh lg:w-full lg:max-w-[662px]',
          'lg:translate-x-0 lg:translate-y-0 lg:rounded-none',
          'flex flex-col',
          // Open/close animation (Radix only toggles data-state; keyframes live
          // in @eureca/ui globals.css). Mobile = slide from bottom (bottom
          // sheet); desktop = slide from the right (side panel). Radix Presence
          // holds the node until the exit animation ends, so close completes
          // before unmount.
          'data-[state=open]:animate-sheet-in-bottom data-[state=closed]:animate-sheet-out-bottom',
          'lg:data-[state=open]:animate-sheet-in-right lg:data-[state=closed]:animate-sheet-out-right'
        )}
      >
        <header className="flex flex-row items-center gap-1.5 px-6 pt-6 pb-2">
          <DialogTitle className="flex-1 text-lg font-semibold leading-none tracking-tight text-foreground">
            {title}
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Fechar"
              className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-border/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        </header>

        <form
          className="flex flex-1 flex-col overflow-hidden"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
          noValidate
        >
          <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 pb-6">
            <FormField error={Boolean(errors.name)}>
              <FormFieldLabel className="text-foreground">Nome</FormFieldLabel>
              <Input
                value={values.name}
                placeholder="Ex.: Informações Adicionais"
                variant={errors.name ? 'destructive' : 'default'}
                onChange={(event) => onFieldChange('name', event.target.value)}
                data-testid="phase-sheet-name"
              />
              <FormFieldError className="text-sm leading-5 text-muted-foreground">
                {errors.name}
              </FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.type)}>
              <FormFieldLabel className="text-foreground">Tipo</FormFieldLabel>
              <NativeSelect
                value={values.type}
                onChange={(event) => onFieldChange('type', event.target.value)}
                disabled={hasAnswers}
                data-testid="phase-sheet-type"
              >
                <option value="" disabled>
                  Selecione uma opção
                </option>
                {STAGE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </NativeSelect>
              {hasAnswers ? (
                <FormFieldHint>
                  Etapa com respostas: o tipo e o teste não podem ser alterados.
                </FormFieldHint>
              ) : null}
              <FormFieldError className="text-sm leading-5 text-muted-foreground">
                {errors.type}
              </FormFieldError>
            </FormField>

            {/* Campos dependentes do Tipo. Aparecem assim que o consultor
                escolhe um Tipo — tanto em "Adicionar nova etapa" quanto no
                "Editar etapa" — para que a etapa seja criada já completa num
                único passo (catálogo, conteúdo, datas, horário, canResubmit).
                Antes (EU-165 Juliana follow-up) ficavam atrás de `isEdit`, o
                que escondia tudo no add e forçava uma etapa intermediária
                meio-configurada — bug reportado: campos não apareciam após o
                Tipo e a criação não persistia. */}
            {values.type ? (
              // Eases in quando o Tipo é selecionado, então a revelação dos
              // campos lê como uma mudança suave dentro do mesmo sheet.
              <div className="flex flex-col gap-5 animate-sheet-content-in">
                {values.type === 'form' ? (
                  <FormField error={Boolean(errors.formId)}>
                    <FormFieldLabel className="text-foreground">
                      Selecione o formulário
                    </FormFieldLabel>
                    <NativeSelect
                      value={values.formId ?? ''}
                      onChange={(event) => onFieldChange('formId', event.target.value)}
                      disabled={hasAnswers}
                      data-testid="phase-sheet-form"
                    >
                      <option value="" disabled>
                        Selecione uma opção
                      </option>
                      {formOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </NativeSelect>
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.formId}
                    </FormFieldError>
                  </FormField>
                ) : null}

                {values.type === 'teste' ? (
                  <FormField error={Boolean(errors.testId)}>
                    <FormFieldLabel className="text-foreground">Selecione o teste</FormFieldLabel>
                    {/*
                      EU-764 — este é o único campo do sheet que deixou de ser
                      `<select>` nativo. Não é preferência estética: `<option>`
                      não aceita tooltip nem `aria-describedby`, e a RN-08/RN-10
                      exigem que a opção bloqueada explique POR QUE está
                      bloqueada, no hover e no foco por teclado. Os demais
                      campos seguem nativos.
                    */}
                    <Combobox
                      options={testOptions.map((option) => ({
                        value: option.value,
                        label: option.label,
                        disabled: option.eligible === false,
                        disabledReason:
                          option.eligible === false ? TEST_INELIGIBLE_COPY : undefined,
                      }))}
                      value={values.testId ?? ''}
                      onSelect={(value) => onFieldChange('testId', value)}
                      // MAP-061 — enquanto a lista não chega, o campo mostra
                      // que está carregando e NÃO abre: abrir com lista parcial
                      // deixaria o consultor escolher sem ver o Mapa (ou vê-lo
                      // sem a regra de elegibilidade aplicada). Mesmo padrão do
                      // campo de teste Kolab logo abaixo.
                      disabled={hasAnswers || testsLoading}
                      error={Boolean(errors.testId)}
                      placeholder={testsLoading ? 'Carregando testes…' : 'Selecione uma opção'}
                      searchPlaceholder="Buscar teste..."
                      loading={testsLoading}
                      errorMessage={testsError ? TESTS_LOAD_ERROR_COPY : undefined}
                      emptyMessage="Nenhum teste disponível para esta vaga."
                      data-testid="phase-sheet-test"
                    />
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.testId}
                    </FormFieldError>
                  </FormField>
                ) : null}

                {values.type === 'fator-h' ? (
                  <FormField error={Boolean(errors.fatorHId)}>
                    <FormFieldLabel className="text-foreground">Selecione o Fator H</FormFieldLabel>
                    <NativeSelect
                      value={values.fatorHId ?? ''}
                      onChange={(event) => onFieldChange('fatorHId', event.target.value)}
                      disabled={hasAnswers}
                      data-testid="phase-sheet-fator-h"
                    >
                      <option value="" disabled>
                        Selecione uma opção
                      </option>
                      {fatorHOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </NativeSelect>
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.fatorHId}
                    </FormFieldError>
                  </FormField>
                ) : null}

                {values.type === 'teste-externo' || values.type === 'fator-h' ? (
                  <>
                    {/* "Como o teste será exibido" — teste-externo é AUTOMÁTICO
                        (via URL → iframe; Kolab → mesma aba com retorno; hint
                        sob o select de Plataforma). Fator H mantém a escolha
                        manual (comportamento pré-EU-539). */}
                    {values.type === 'fator-h' ? (
                      <FormField error={Boolean(errors.externalEmbedMode)}>
                        <FormFieldLabel className="text-foreground">
                          Como o teste será exibido
                        </FormFieldLabel>
                        <NativeSelect
                          value={values.externalEmbedMode ?? ''}
                          onChange={(event) =>
                            onFieldChange(
                              'externalEmbedMode',
                              event.target.value as PhaseStageDraft['externalEmbedMode']
                            )
                          }
                          data-testid="phase-sheet-external-mode"
                        >
                          <option value="" disabled>
                            Selecione uma opção
                          </option>
                          <option value="embed">Embedado na tela (iframe)</option>
                          <option value="nova-aba">Em uma nova aba</option>
                        </NativeSelect>
                        <FormFieldError className="text-sm leading-5 text-muted-foreground">
                          {errors.externalEmbedMode}
                        </FormFieldError>
                      </FormField>
                    ) : null}
                    {/* URL é estática só para teste-externo genérico. No Fator H a
                        URL do embed é gerada por candidato em runtime (mintada no
                        candidate BFF ao abrir o módulo), então não há o que digitar.
                        EU-539 — teste-externo ganha o seletor de Plataforma:
                        'url' (genérica) vs 'kolab' (instância do catálogo Kolab;
                        URL gerada por candidato, como no Fator H). */}
                    {values.type === 'teste-externo' ? (
                      <>
                        <FormField>
                          <FormFieldLabel className="text-foreground">Plataforma</FormFieldLabel>
                          <NativeSelect
                            value={values.externalPlatform || 'url'}
                            onChange={(event) =>
                              onFieldChange(
                                'externalPlatform',
                                event.target.value as PhaseStageDraft['externalPlatform']
                              )
                            }
                            disabled={hasAnswers}
                            data-testid="phase-sheet-external-platform"
                          >
                            <option value="url">Outra plataforma (via URL)</option>
                            <option value="kolab">Kolab</option>
                          </NativeSelect>
                          {/* Exibição automática por plataforma (sem select). */}
                          <FormFieldHint data-testid="phase-sheet-display-mode-hint">
                            {(values.externalPlatform || 'url') === 'kolab'
                              ? 'O teste abre na mesma aba e o candidato retorna ao concluir.'
                              : 'O teste é exibido embedado na tela (iframe).'}
                          </FormFieldHint>
                        </FormField>
                        {(values.externalPlatform || 'url') === 'kolab' ? (
                          <FormField error={Boolean(errors.kolabInstanceId)}>
                            <FormFieldLabel className="text-foreground">
                              Selecione o teste Kolab
                            </FormFieldLabel>
                            <NativeSelect
                              value={values.kolabInstanceId ?? ''}
                              onChange={(event) =>
                                onFieldChange('kolabInstanceId', event.target.value)
                              }
                              disabled={hasAnswers || kolabCatalogueState !== 'ready'}
                              data-testid="phase-sheet-kolab-instance"
                            >
                              <option value="" disabled>
                                {kolabCatalogueState === 'loading'
                                  ? 'Carregando testes…'
                                  : kolabCatalogueState === 'error'
                                    ? 'Erro ao carregar os testes'
                                    : kolabOptions.length === 0
                                      ? 'Nenhum teste disponível'
                                      : 'Selecione uma opção'}
                              </option>
                              {kolabOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </NativeSelect>
                            {kolabCatalogueState === 'error' ? (
                              <FormFieldHint className="text-destructive">
                                Não foi possível listar os testes no Kolab. Tente novamente mais
                                tarde.
                              </FormFieldHint>
                            ) : kolabCatalogueState === 'ready' && kolabOptions.length === 0 ? (
                              <FormFieldHint>
                                Nenhuma instância Kolab disponível para este cliente.
                              </FormFieldHint>
                            ) : (
                              <FormFieldHint>
                                A URL do teste é gerada automaticamente por candidato ao abrir o
                                módulo.
                              </FormFieldHint>
                            )}
                            <FormFieldError className="text-sm leading-5 text-muted-foreground">
                              {errors.kolabInstanceId}
                            </FormFieldError>
                          </FormField>
                        ) : (
                          <FormField error={Boolean(errors.externalUrl)}>
                            <FormFieldLabel className="text-foreground">
                              URL da plataforma
                            </FormFieldLabel>
                            <Input
                              value={values.externalUrl ?? ''}
                              placeholder="https://..."
                              variant={errors.externalUrl ? 'destructive' : 'default'}
                              onChange={(event) => onFieldChange('externalUrl', event.target.value)}
                              data-testid="phase-sheet-external-url"
                            />
                            <FormFieldError className="text-sm leading-5 text-muted-foreground">
                              {errors.externalUrl}
                            </FormFieldError>
                          </FormField>
                        )}
                      </>
                    ) : (
                      <p className="text-sm leading-5 text-muted-foreground">
                        A URL do exame é gerada automaticamente por candidato ao abrir o módulo —
                        não é necessário informá-la aqui.
                      </p>
                    )}
                  </>
                ) : null}

                {values.type === 'conteudo' ? (
                  <>
                    <FormField error={Boolean(errors.contentText)}>
                      <FormFieldLabel className="text-foreground">Conteúdo textual</FormFieldLabel>
                      <RichTextEditor
                        value={values.contentText ?? ''}
                        onChange={(html) => onFieldChange('contentText', html)}
                      />
                      <FormFieldHint>Campo opcional</FormFieldHint>
                    </FormField>
                    <FormField error={Boolean(errors.contentFile || fileError)}>
                      <FormFieldLabel className="text-foreground">Upload de arquivo</FormFieldLabel>
                      <FileInput
                        fileName={values.contentFileName ?? null}
                        onChange={(file) => {
                          setFileError('');
                          onContentFileChange(file);
                        }}
                        onError={(message) => setFileError(message)}
                      />
                      {fileError ? (
                        <FormFieldError className="text-sm leading-5">{fileError}</FormFieldError>
                      ) : values.contentUploading ? (
                        <FormFieldHint>Enviando arquivo…</FormFieldHint>
                      ) : (
                        <FormFieldHint>
                          Envie em .PDF e .MP4, com no máximo 20 MB. Campo opcional.
                        </FormFieldHint>
                      )}
                    </FormField>
                  </>
                ) : null}

                <FormField error={Boolean(errors.startDate || errors.endDate)}>
                  <FormFieldLabel className="text-foreground">Duração</FormFieldLabel>
                  <div className="flex flex-row items-center gap-3">
                    <DatePicker
                      value={values.startDate}
                      aria-label="Data de início"
                      data-testid="phase-sheet-start-date"
                      hasError={Boolean(errors.startDate)}
                      onChange={(value) => onFieldChange('startDate', value)}
                    />
                    <span className="shrink-0 text-xs text-muted-foreground">até</span>
                    <DatePicker
                      value={values.endDate}
                      aria-label="Data de encerramento"
                      data-testid="phase-sheet-end-date"
                      hasError={Boolean(errors.endDate)}
                      onChange={(value) => onFieldChange('endDate', value)}
                    />
                  </div>
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.startDate || errors.endDate}
                  </FormFieldError>
                </FormField>

                <FormField error={Boolean(errors.endTime)}>
                  <FormFieldLabel className="text-foreground">
                    Horário de encerramento
                  </FormFieldLabel>
                  <Input
                    type="time"
                    value={values.endTime ?? ''}
                    placeholder="HH:MM"
                    onChange={(event) => onFieldChange('endTime', event.target.value)}
                  />
                  <FormFieldHint>Campo opcional</FormFieldHint>
                </FormField>

                <FormField error={Boolean(errors.canResubmit)}>
                  <FormFieldLabel className="text-foreground">
                    Será possível reenviar a resposta?
                  </FormFieldLabel>
                  {/* RN-13/RN-14 — para Teste "fixo" (Raio-X Comportamental,
                      Fit cultural, Raio X de Carreira), Currículo e Conteúdo o
                      campo fica travado: desabilitado e fixado no valor que a
                      regra de negócio impõe (ver `resubmit-lock.ts`). */}
                  <RadioCardGroup
                    value={resubmitValue}
                    onValueChange={(value) =>
                      onFieldChange('canResubmit', value as PhaseStageDraft['canResubmit'])
                    }
                    className="flex flex-row gap-3"
                    disabled={resubmitLock.locked}
                    data-testid="phase-sheet-resubmit"
                  >
                    <ResubmitOption value="sim" label="Sim" />
                    <ResubmitOption value="nao" label="Não" />
                  </RadioCardGroup>
                  {resubmitLock.locked && resubmitLock.reason ? (
                    <FormFieldHint>{resubmitLock.reason}</FormFieldHint>
                  ) : (
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.canResubmit}
                    </FormFieldError>
                  )}
                </FormField>
              </div>
            ) : null}
          </div>

          <footer className="flex flex-row justify-between gap-2 border-t border-border bg-card px-6 py-4">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Voltar
              </Button>
            </DialogClose>
            <Button type="submit" loading={isSubmitting} data-testid="phase-sheet-submit">
              {submitLabel}
            </Button>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ResubmitOptionProps {
  value: string;
  label: string;
}

/**
 * Sim/Não card. Uses `RadioCardItem` (not `RadioGroupItem`) because the
 * card-shaped variant carries `data-[state=checked]:` styling on the
 * button itself, which is the only place Radix actually puts `data-state`.
 * The previous attempt nested a button inside a `<label>` (invalid HTML —
 * button-inside-label — caused click-handling issues) and used
 * `RadioGroupItem` which delegates checked styling to a span child whose
 * `data-state` never updates. Adding `group` so the inner `RadioDot`
 * picks up the parent's checked state via `group-data-[state=checked]:`.
 */
function ResubmitOption({ value, label }: ResubmitOptionProps) {
  return (
    <RadioCardItem
      value={value}
      className={cn('group flex-1 justify-start rounded-xl p-4 hover:border-primary/60')}
    >
      <span className="text-sm font-medium leading-none text-foreground">{label}</span>
    </RadioCardItem>
  );
}

interface FileInputProps {
  fileName: string | null;
  onChange: (file: File | null) => void;
  /** Mensagem de erro inline (extensão inválida, tamanho excedido, etc.). */
  onError?: (message: string) => void;
}

// EU-165 Juliana TODO #3 — validação client-side. `accept=` é só hint do
// browser; aqui rejeitamos pelo MIME type + tamanho real do `File`.
const ALLOWED_MIME_TYPES = new Set(['application/pdf', 'video/mp4']);
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

function FileInput({ fileName, onChange, onError }: FileInputProps) {
  return (
    <label className="flex h-9 w-full cursor-pointer flex-row items-center gap-2 rounded-md border border-input bg-background px-3 text-sm shadow-2xs transition-colors hover:bg-muted/30">
      <span className="flex-1 truncate text-foreground">
        {fileName ?? 'Clique para selecionar um arquivo'}
      </span>
      <input
        type="file"
        accept="application/pdf,video/mp4"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null;
          // Reset do input pra permitir re-selecionar o mesmo arquivo (e.g.
          // depois de uma rejeição por tamanho/extensão e o usuário corrigir).
          event.target.value = '';
          if (!file) {
            onChange(null);
            return;
          }
          if (!ALLOWED_MIME_TYPES.has(file.type)) {
            onError?.('Apenas arquivos .PDF e .MP4 são aceitos');
            return;
          }
          if (file.size > MAX_FILE_SIZE_BYTES) {
            onError?.('O arquivo excede o limite de 20 MB');
            return;
          }
          onChange(file);
        }}
      />
    </label>
  );
}
